// Shared "Log client activity" modal.
//
// Promoted out of pages/activities/ActivitiesPage.tsx (where it was
// file-private) so the Leads inbox can launch it pre-filled from a lead and
// pass `linkedLeadId` — wiring the activity to the lead timeline + the
// client_activity_log BQ mirror.
//
// Behaviour on the standalone Activities page is unchanged: no `linkedLeadId`,
// no `prefill`, no "Mark contacted" checkbox.
import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { ActivityType } from '../../hooks/useActivities';
import { ACTIVITY_META } from './activityMeta';

export interface LogActivitySubmitArgs {
  activityType: ActivityType;
  outcome?: string;
  clientName?: string;
  clientPhone?: string | null;
  clientEmail?: string | null;
  notes?: string;
  durationSeconds?: number;
  followUpAt?: string;
  // Set when launched from a lead — wires the activity to the lead timeline.
  linkedLeadId?: string;
  // True when the operator ticked "Mark lead as contacted". The caller decides
  // whether to act on it (only meaningful for a 'new' lead).
  markContacted?: boolean;
}

export const LogActivityModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (args: LogActivitySubmitArgs) => Promise<void>;
  // When set, the activity is wired to this lead (passed through to logActivity).
  linkedLeadId?: string;
  // Seed the client fields from the originating lead. Editable by the operator.
  prefill?: { clientName?: string; clientPhone?: string | null; clientEmail?: string | null };
  // Current lead status — the "Mark contacted" checkbox only shows for 'new'.
  leadStatus?: string;
}> = ({ open, onClose, onSubmit, linkedLeadId, prefill, leadStatus }) => {
  const [activityType, setActivityType] = useState<ActivityType>('call_outbound');
  const [outcome, setOutcome] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [markContacted, setMarkContacted] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The "Mark lead as contacted" affordance only makes sense for a NEW lead
  // launched from the inbox (updateLead idempotently stamps firstContactedAt).
  const showMarkContacted = Boolean(linkedLeadId) && leadStatus === 'new';

  // Seed/refresh client fields from the lead each time the modal opens. Reset
  // when launched without a prefill (standalone Activities page).
  useEffect(() => {
    if (!open) return;
    setClientName(prefill?.clientName ?? '');
    setClientPhone(prefill?.clientPhone ?? '');
    setClientEmail(prefill?.clientEmail ?? '');
    setMarkContacted(true);
    // Deliberately do not reset activityType/outcome/notes here — operator may
    // reopen to amend; reset() on submit handles the full clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setActivityType('call_outbound');
    setOutcome('');
    setClientName('');
    setClientPhone('');
    setClientEmail('');
    setNotes('');
    setDurationMinutes('');
    setFollowUpDate('');
    setMarkContacted(true);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const durationSeconds = durationMinutes
        ? Math.max(0, Math.round(parseFloat(durationMinutes) * 60))
        : undefined;
      const followUpAt = followUpDate
        ? new Date(followUpDate).toISOString()
        : undefined;
      await onSubmit({
        activityType,
        outcome: outcome.trim() || undefined,
        clientName: clientName.trim() || undefined,
        clientPhone: clientPhone.trim() || null,
        clientEmail: clientEmail.trim() || null,
        notes: notes.trim() || undefined,
        durationSeconds,
        followUpAt,
        linkedLeadId,
        markContacted: showMarkContacted ? markContacted : false,
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">
              {linkedLeadId ? 'Log contact for this lead' : 'Log client activity'}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Captures touchpoints with clients — feeds the weekly funnel and
              engagement-gap detection.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Activity type
            </label>
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value as ActivityType)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
            >
              {(Object.keys(ACTIVITY_META) as ActivityType[]).map((k) => (
                <option key={k} value={k}>
                  {ACTIVITY_META[k].label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Client name
              </label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Outcome
              </label>
              <input
                type="text"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                placeholder="connected, no_answer, …"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="+61 4…"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Email
              </label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="jane@example.com"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Duration (min)
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                placeholder="15"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
                Follow-up date
              </label>
              <input
                type="datetime-local"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Discussed refinance options, sent hello pack…"
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          {showMarkContacted && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="checkbox"
                checked={markContacted}
                onChange={(e) => setMarkContacted(e.target.checked)}
                className="accent-blue-500"
              />
              Mark lead as contacted
            </label>
          )}

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
              {submitting ? 'Logging…' : 'Log activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogActivityModal;
